import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Typography,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Import components
import EncodeTab from './components/EncodeTab';
import DecodeTab from './components/DecodeTab';
import BatchTab from './components/BatchTab';
import AnalyzeTab from './components/AnalyzeTab';
import TerminalConsole from './components/TerminalConsole';
import Footer from './components/Footer';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import Analytics from './components/Analytics';
import CookieBanner from './components/CookieBanner';
import About from './pages/About';
import Security from './pages/Security';
import HowItWorksPage from './pages/HowItWorks';
import FAQ from './pages/FAQ';
import HowToHideMessage from './pages/HowToHideMessage';
import HowToEncryptImage from './pages/HowToEncryptImage';
import WhatIsSteganography from './pages/WhatIsSteganography';


const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00F0FF',
    },
    secondary: {
      main: '#FF007A',
    },
    warning: {
      main: '#FFD200',
    },
    success: {
      main: '#39FF14',
    },
    background: {
      default: '#0B0813',
      paper: '#0B0813',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#6B6A78',
    },
  },
  typography: {
    fontFamily: '"JetBrains Mono", "Courier New", monospace',
    h1: {
      fontFamily: '"Press Start 2P", "DCPixel", cursive',
      textTransform: 'uppercase',
    },
    h2: {
      fontFamily: '"Press Start 2P", "DCPixel", cursive',
      textTransform: 'uppercase',
    },
    h3: {
      fontFamily: '"Press Start 2P", "DCPixel", cursive',
      textTransform: 'uppercase',
    },
    h4: {
      fontFamily: '"Press Start 2P", "DCPixel", cursive',
      textTransform: 'uppercase',
    },
    h5: {
      fontFamily: '"Press Start 2P", "DCPixel", cursive',
      textTransform: 'uppercase',
    },
    h6: {
      fontFamily: '"Press Start 2P", "DCPixel", cursive',
      textTransform: 'uppercase',
    },
    body1: {
      fontFamily: '"JetBrains Mono", "Courier New", monospace',
    },
    body2: {
      fontFamily: '"JetBrains Mono", "Courier New", monospace',
    },
  },
});

// Styled components for NEXUS design
const NexusContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#0B0813',
  position: 'relative',
  overflow: 'hidden',
  fontFamily: theme.typography.body1.fontFamily,
}));

const CrtOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 9999,
  background: 'repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15) 1px, transparent 1px, transparent 2px)',
}));

const FallingBlocks = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 1,
  overflow: 'hidden',
}));

const NavigationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 40px',
  borderBottom: '1px solid #00F0FF',
  backgroundColor: 'rgba(11, 8, 19, 0.95)',
  position: 'relative',
  zIndex: 100,
  [theme.breakpoints.down('md')]: {
    padding: '15px 20px',
    flexDirection: 'column',
    gap: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 15px',
  },
}));

const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
}));

const LogoLetter = styled(Box)(({ theme }) => ({
  fontSize: '48px',
  fontWeight: 'bold',
  color: '#39FF14',
  textShadow: '0 0 10px #39FF14, 0 0 20px #39FF14',
  fontFamily: '"Press Start 2P", cursive',
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#FFFFFF',
  fontFamily: '"Press Start 2P", cursive',
  letterSpacing: '2px',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    letterSpacing: '1px',
  },
}));

const NavigationTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '10px',
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '5px',
    width: '100%',
  },
}));

const NavTab = styled(Button)<{ active: boolean }>(({ active, theme }) => ({
  border: active ? '2px solid #00F0FF' : '2px solid #6B6A78',
  color: active ? '#00F0FF' : '#6B6A78',
  padding: '10px 20px',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '8px 15px',
    fontSize: '9px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 10px',
    fontSize: '8px',
    minWidth: 'unset',
  },
  '&:hover': {
    borderColor: '#00F0FF',
    color: '#00F0FF',
    boxShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    backgroundColor: '#00F0FF',
    color: '#0B0813',
  },
}));

const HeroSection = styled(Box)(({ theme }) => ({
  padding: '60px 40px',
  position: 'relative',
  zIndex: 10,
  [theme.breakpoints.down('md')]: {
    padding: '40px 20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '30px 15px',
  },
}));

const HeroHeadline = styled(Typography)(({ theme }) => ({
  fontSize: '42px',
  fontWeight: 'bold',
  color: '#FFFFFF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '30px',
  textShadow: '0 0 20px rgba(0, 240, 255, 0.5)',
  lineHeight: 1.5,
  [theme.breakpoints.down('lg')]: {
    fontSize: '32px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '24px',
    marginBottom: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    marginBottom: '15px',
    lineHeight: 1.3,
  },
}));

const StatusCounterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '40px',
  marginTop: '30px',
  [theme.breakpoints.down('md')]: {
    gap: '20px',
    marginTop: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '15px',
    marginTop: '15px',
  },
}));

const StatusCounter = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
}));

const CounterValue = styled(Typography)(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 'bold',
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  textShadow: '0 0 10px rgba(57, 255, 20, 0.5)',
  [theme.breakpoints.down('md')]: {
    fontSize: '28px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
  },
}));

const CounterLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  [theme.breakpoints.down('md')]: {
    fontSize: '10px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '8px',
    letterSpacing: '0.5px',
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: '40px',
  position: 'relative',
  zIndex: 10,
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
  padding: '15px',
  },
}));

const ContentPanel = styled(Box)<{ active: boolean }>(({ active, theme }) => ({
  display: active ? 'block' : 'none',
  border: '1px solid #00F0FF',
  backgroundColor: 'rgba(11, 8, 19, 0.8)',
  padding: '30px',
  borderRadius: '5px',
  boxShadow: '0 0 20px rgba(0, 240, 255, 0.1)',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}));

function AppContent() {
  const [activeTab, setActiveTab] = useState(0);
  const [blocks, setBlocks] = useState<Array<{ id: number; x: number; y: number; color: string; speed: number }>>([]);
  const [stats, setStats] = useState({ imagesProcessed: 0, messagesEncoded: 0, qualityScore: 95 });

  useEffect(() => {
    // Create falling blocks
    const interval = setInterval(() => {
      const colors = ['#00F0FF', '#FF007A', '#FFD200', '#39FF14'];
      const newBlock = {
        id: Date.now(),
        x: Math.random() * 100,
        y: -10,
        color: colors[Math.floor(Math.random() * colors.length)],
        speed: 0.5 + Math.random() * 1.5,
      };
      setBlocks(prev => [...prev.slice(-20), newBlock]);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Remove hash-based routing - using React Router instead

  // Function to update stats when images are processed
  const incrementImagesProcessed = () => {
    setStats(prev => ({ ...prev, imagesProcessed: prev.imagesProcessed + 1 }));
  };

  const incrementMessagesEncoded = () => {
    setStats(prev => ({ ...prev, messagesEncoded: prev.messagesEncoded + 1 }));
  };

  const tabs = [
    { id: 0, label: 'ENCODE', icon: '🔒' },
    { id: 1, label: 'DECODE', icon: '🔓' },
    { id: 2, label: 'BATCH', icon: '📦' },
    { id: 3, label: 'ANALYZE', icon: '📊' },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CookieBanner />
      <Analytics />
      <NexusContainer>
        <CrtOverlay />
        <FallingBlocks>
          {blocks.map((block) => (
            <Box
              key={block.id}
              sx={{
                position: 'absolute',
                left: `${block.x}%`,
                top: `${block.y}%`,
                width: '20px',
                height: '20px',
                backgroundColor: block.color,
                opacity: 0.3,
                boxShadow: `0 0 10px ${block.color}`,
                animation: 'fall 5s linear infinite',
                '@keyframes fall': {
                  '0%': { top: '-10%' },
                  '100%': { top: '110%' },
                },
              }}
            />
          ))}
        </FallingBlocks>

        <NavigationHeader>
          <Logo>
            <LogoLetter>🔐</LogoLetter>
            <LogoText>StegSafe</LogoText>
          </Logo>

          <NavigationTabs>
            {tabs.map((tab) => (
              <NavTab
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon} {tab.label}
              </NavTab>
            ))}
          </NavigationTabs>
        </NavigationHeader>

        <HeroSection>
          <Box sx={{ 
            display: 'flex', 
            gap: '40px', 
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            <Box sx={{ flex: 1, width: '100%' }}>
              <HeroHeadline>
                SECURE IMAGE STEGANOGRAPHY WITH ENCRYPTION<br />
                <span style={{ color: '#39FF14' }}>HIDE SECRET MESSAGES IN IMAGES</span>
              </HeroHeadline>

              <StatusCounterBar>
                <StatusCounter>
                  <CounterValue>{stats.imagesProcessed}</CounterValue>
                  <CounterLabel>IMAGES PROCESSED</CounterLabel>
                </StatusCounter>
                <StatusCounter>
                  <CounterValue>{stats.messagesEncoded}</CounterValue>
                  <CounterLabel>MESSAGES ENCODED</CounterLabel>
                </StatusCounter>
                <StatusCounter>
                  <CounterValue>{stats.qualityScore}%</CounterValue>
                  <CounterLabel>IMAGE QUALITY SCORE</CounterLabel>
                </StatusCounter>
              </StatusCounterBar>
            </Box>

            <Box sx={{ 
              width: { xs: '100%', md: '400px' }, 
              flexShrink: 0 
            }}>
              <TerminalConsole />
            </Box>
          </Box>
        </HeroSection>

        <Routes>
          <Route path="/" element={
            <>
              <MainContent>
                <ContentPanel active={activeTab === 0}>
                  <EncodeTab onImageProcessed={incrementImagesProcessed} onMessageEncoded={incrementMessagesEncoded} />
                </ContentPanel>

                <ContentPanel active={activeTab === 1}>
                  <DecodeTab onImageProcessed={incrementImagesProcessed} />
                </ContentPanel>

                <ContentPanel active={activeTab === 2}>
                  <BatchTab onImageProcessed={incrementImagesProcessed} onMessageEncoded={incrementMessagesEncoded} />
                </ContentPanel>

                <ContentPanel active={activeTab === 3}>
                  <AnalyzeTab onImageProcessed={incrementImagesProcessed} />
                </ContentPanel>
              </MainContent>
            </>
          } />
          <Route path="/about" element={<About />} />
          <Route path="/security" element={<Security />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/how-to-hide-message-in-image" element={<HowToHideMessage />} />
          <Route path="/how-to-encrypt-an-image" element={<HowToEncryptImage />} />
          <Route path="/what-is-steganography" element={<WhatIsSteganography />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>

        <Footer />
      </NexusContainer>
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
